import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Users, Image, Gift, Settings, Menu, X, 
  IceCream, TrendingUp, Award, Clock, RefreshCw, Plus,
  Edit, Trash2, Search, ChevronRight, ExternalLink, Lock, Mail, LogOut, Shield, Link2, Copy, CheckCircle2,
  RotateCcw, Archive
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { adminAPI, customerAPI, promotionAPI, rewardAPI } from '@/services/api';

// Sidebar Navigation
const navItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { path: '/admin/customers', icon: Users, label: 'Clientes' },
  { path: '/admin/promotions', icon: Image, label: 'Promociones' },
  { path: '/admin/rewards', icon: Gift, label: 'Recompensas' },
  { path: '/admin/admins', icon: Shield, label: 'Administradores', adminOnly: true },
  { path: '/admin/odoo', icon: Link2, label: 'Odoo Webhook' },
];

// Admin Login Component
const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Ingresa email y contraseña');
      return;
    }

    setLoading(true);
    try {
      const response = await adminAPI.login({ email: email.trim().toLowerCase(), password });
      if (response.data.success) {
        localStorage.setItem('darkar_admin', JSON.stringify(response.data.admin));
        toast.success(response.data.message);
        onLogin(response.data.admin);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Credenciales incorrectas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-2 border-rose-soft rounded-3xl shadow-soft-pop">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 rounded-2xl bg-rose-pop flex items-center justify-center mx-auto mb-4">
            <IceCream className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="font-nunito text-2xl text-gray-900">Panel de Administración</CardTitle>
          <CardDescription className="font-quicksand">Ingresa tus credenciales de administrador</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="relative">
              <Input
                type="email"
                placeholder="Email de administrador"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-12 py-5 rounded-xl border-2 border-rose-soft focus:border-rose-pop"
                data-testid="admin-email-input"
              />
              <div className="absolute left-4 top-1/2 -translate-y-1/2">
                <Mail className="w-5 h-5 text-rose-pop" />
              </div>
            </div>
            
            <div className="relative">
              <Input
                type="password"
                placeholder="Contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-12 py-5 rounded-xl border-2 border-rose-soft focus:border-rose-pop"
                data-testid="admin-password-input"
              />
              <div className="absolute left-4 top-1/2 -translate-y-1/2">
                <Lock className="w-5 h-5 text-rose-pop" />
              </div>
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-rose-pop hover:bg-rose-dark text-white font-bold py-5 rounded-xl"
              data-testid="admin-login-btn"
            >
              {loading ? 'Verificando...' : 'Iniciar Sesión'}
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-500 hover:text-rose-pop font-quicksand"
            >
              ← Volver a la app
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const AdminPanel = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [admin, setAdmin] = useState(() => {
    const saved = localStorage.getItem('darkar_admin');
    return saved ? JSON.parse(saved) : null;
  });
  const [verifying, setVerifying] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  // Verify admin session on mount
  useEffect(() => {
    const verifyAdmin = async () => {
      if (!admin?.id) {
        setVerifying(false);
        return;
      }

      try {
        await adminAPI.verify(admin.id);
        setVerifying(false);
      } catch (error) {
        // Invalid session, clear it
        localStorage.removeItem('darkar_admin');
        setAdmin(null);
        setVerifying(false);
      }
    };

    verifyAdmin();
  }, [admin?.id]);

  const handleLogout = () => {
    localStorage.removeItem('darkar_admin');
    setAdmin(null);
    toast.info('Sesión cerrada');
  };

  // Show loading while verifying
  if (verifying) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-rose-pop border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Show login if not authenticated
  if (!admin) {
    return <AdminLogin onLogin={setAdmin} />;
  }

  return (
    <div className="min-h-screen bg-cream flex">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-white shadow-soft-pop
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-6 border-b border-rose-soft">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-pop flex items-center justify-center">
              <IceCream className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-nunito font-bold text-lg text-gray-900">Darkar Co.</h1>
              <p className="text-xs text-gray-500">Panel Admin</p>
            </div>
          </div>
        </div>

        <nav className="p-4 space-y-1">
          {navItems
            .filter(item => !item.adminOnly || admin?.role === 'admin')
            .map((item) => {
            const isActive = item.exact 
              ? location.pathname === item.path
              : location.pathname.startsWith(item.path) && item.path !== '/admin';
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-xl
                  font-quicksand font-medium transition-all
                  ${isActive 
                    ? 'bg-rose-pop text-white shadow-soft-pop' 
                    : 'text-gray-600 hover:bg-rose-faint hover:text-rose-pop'
                  }
                `}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-rose-soft space-y-3">
          <div className="text-xs text-gray-500 font-quicksand">
            <div className="flex items-center gap-2 mb-1">
              <p className="font-medium text-gray-700">{admin?.name}</p>
              <Badge className={admin?.role === 'admin' ? 'bg-rose-pop text-white text-[10px]' : 'bg-amber-500 text-white text-[10px]'}>
                {admin?.role === 'admin' ? 'Admin' : 'Gerente'}
              </Badge>
            </div>
            <p className="truncate">{admin?.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-rose-pop transition-colors"
              data-testid="view-customer-app"
            >
              <ExternalLink className="w-4 h-4" />
              App
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-500 transition-colors ml-auto"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Salir
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        {/* Top Bar */}
        <header className="bg-white shadow-sm sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 py-3 lg:px-6">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </Button>
            
            <h2 className="font-nunito font-bold text-lg text-gray-900 lg:text-xl">
              {navItems.find(item => 
                item.exact 
                  ? location.pathname === item.path
                  : location.pathname.startsWith(item.path) && item.path !== '/admin'
              )?.label || 'Dashboard'}
            </h2>

            <div className="w-10" />
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          <Routes>
            <Route index element={<DashboardPage />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="promotions" element={<PromotionsPage />} />
            <Route path="rewards" element={<RewardsPage />} />
            <Route path="admins" element={<AdminsPage />} />
            <Route path="odoo" element={<OdooWebhookPage />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

// Dashboard Page
const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [odooStatus, setOdooStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, odooRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getOdooStatus()
      ]);
      setStats(statsRes.data);
      setOdooStatus(odooRes.data);
    } catch (error) {
      toast.error('Error cargando estadísticas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const statCards = [
    { 
      label: 'Total Clientes', 
      value: stats?.total_customers || 0, 
      icon: Users, 
      color: 'bg-rose-pop' 
    },
    { 
      label: 'Visitas Hoy', 
      value: stats?.total_visits_today || 0, 
      icon: TrendingUp, 
      color: 'bg-mint' 
    },
    { 
      label: 'Recompensas Canjeadas', 
      value: stats?.total_rewards_claimed || 0, 
      icon: Award, 
      color: 'bg-yellow-accent' 
    },
    { 
      label: 'Cerca de Premio', 
      value: stats?.customers_near_reward || 0, 
      icon: Clock, 
      color: 'bg-rose-soft' 
    },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <Card key={index} className="border-2 border-rose-soft rounded-2xl overflow-hidden">
            <CardContent className="p-4">
              <div className={`w-10 h-10 ${stat.color} rounded-xl flex items-center justify-center mb-3`}>
                <stat.icon className="w-5 h-5 text-white" />
              </div>
              <p className="text-2xl font-nunito font-bold text-gray-900">
                {loading ? '-' : stat.value}
              </p>
              <p className="text-sm text-gray-500 font-quicksand">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Odoo Status */}
      <Card className="border-2 border-rose-soft rounded-2xl">
        <CardHeader>
          <CardTitle className="font-nunito text-lg flex items-center gap-2">
            <Settings className="w-5 h-5 text-rose-pop" />
            Estado de Odoo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${odooStatus?.connected ? 'bg-mint' : 'bg-rose-pop'}`} />
            <span className="font-quicksand text-gray-700">
              {odooStatus?.connected ? 'Conectado' : 'Desconectado'}
            </span>
            <span className="text-sm text-gray-500">
              {odooStatus?.url || 'No configurado'}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Refresh Button */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          onClick={loadData}
          disabled={loading}
          className="border-rose-soft hover:bg-rose-faint rounded-xl"
          data-testid="refresh-stats-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>
    </div>
  );
};

// Customers Page
const CustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [archivedCustomers, setArchivedCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingArchived, setLoadingArchived] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState('');
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, customer: null });
  const [restoreDialog, setRestoreDialog] = useState({ open: false, customer: null });
  const [restoreFormData, setRestoreFormData] = useState({ stickers: 0, gold_coins: 0 });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '1234',
    stickers: 0,
    gold_coins: 0
  });
  const [allCustomers, setAllCustomers] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);
  const PAGE_SIZE = 50;

  const loadCustomers = useCallback(async (reset = false) => {
    setLoading(true);
    try {
      const currentPage = reset ? 0 : page;
      const response = await customerAPI.list({ 
        limit: PAGE_SIZE, 
        skip: currentPage * PAGE_SIZE 
      });
      
      const newCustomers = response.data.customers || response.data;
      const total = response.data.total || newCustomers.length;
      
      if (reset) {
        setAllCustomers(newCustomers);
        setCustomers(newCustomers);
        setPage(1);
      } else {
        setAllCustomers(prev => [...prev, ...newCustomers]);
        setCustomers(prev => [...prev, ...newCustomers]);
        setPage(prev => prev + 1);
      }
      
      setTotalCustomers(total);
      setHasMore(newCustomers.length === PAGE_SIZE);
    } catch (error) {
      toast.error('Error cargando clientes');
    } finally {
      setLoading(false);
    }
  }, [page]);

  const loadMoreCustomers = () => {
    if (!loading && hasMore && !search.trim()) {
      loadCustomers(false);
    }
  };

  // Search with debounce - searches server if text entered, filters locally if cleared
  useEffect(() => {
    if (!search.trim()) {
      setCustomers(allCustomers);
      return;
    }
    
    const searchLower = search.toLowerCase().trim();
    
    // First filter locally for instant feedback
    const localFiltered = allCustomers.filter(customer => {
      const name = (customer.name || '').toLowerCase();
      const email = (customer.email || '').toLowerCase();
      const phone = (customer.phone || '').toLowerCase();
      return name.includes(searchLower) || email.includes(searchLower) || phone.includes(searchLower);
    });
    setCustomers(localFiltered);
    
    // Then search server for complete results (debounced)
    const debounce = setTimeout(async () => {
      if (search.trim().length >= 2) {
        setSearchLoading(true);
        try {
          const response = await customerAPI.list({ search: search.trim(), limit: 100 });
          const serverResults = response.data.customers || response.data;
          setCustomers(serverResults);
        } catch (error) {
          console.error('Search error:', error);
        } finally {
          setSearchLoading(false);
        }
      }
    }, 500);
    
    return () => clearTimeout(debounce);
  }, [search, allCustomers]);

  const loadArchivedCustomers = useCallback(async () => {
    setLoadingArchived(true);
    try {
      const response = await adminAPI.getArchivedCustomers();
      if (response.data.success) {
        setArchivedCustomers(response.data.customers);
      }
    } catch (error) {
      toast.error('Error cargando clientes archivados');
    } finally {
      setLoadingArchived(false);
    }
  }, []);

  useEffect(() => {
    loadCustomers(true);
  }, []);

  useEffect(() => {
    if (showArchived && archivedCustomers.length === 0) {
      loadArchivedCustomers();
    }
  }, [showArchived, loadArchivedCustomers, archivedCustomers.length]);

  const handleSyncToOdoo = async (customerId) => {
    try {
      const response = await adminAPI.syncCustomerToOdoo(customerId);
      if (response.data.success) {
        toast.success(`Cliente sincronizado con Odoo (ID: ${response.data.odoo_id})`);
        loadCustomers();
      } else {
        toast.error('Error sincronizando con Odoo');
      }
    } catch (error) {
      toast.error('Error sincronizando con Odoo');
    }
  };

  const openCreateDialog = () => {
    setFormData({ name: '', email: '', phone: '', password: '1234', stickers: 0, gold_coins: 0 });
    setCreateDialog(true);
  };

  const openEditDialog = (customer) => {
    setSelectedCustomer(customer);
    setFormData({
      stickers: customer.stickers || 0,
      gold_coins: customer.gold_coins || 0
    });
    setEditDialog(true);
  };

  const handleCreateCustomer = async () => {
    if (!formData.name || !formData.email) {
      toast.error('Nombre y email son requeridos');
      return;
    }
    try {
      await adminAPI.createCustomer(formData);
      toast.success('Cliente creado exitosamente');
      setCreateDialog(false);
      loadCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creando cliente');
    }
  };

  const handleUpdateVisits = async () => {
    if (!selectedCustomer) return;
    try {
      await adminAPI.updateStickers(selectedCustomer.id, {
        stickers: formData.stickers,
        gold_coins: formData.gold_coins
      });
      toast.success('Stickers actualizados');
      setEditDialog(false);
      loadCustomers();
    } catch (error) {
      toast.error('Error actualizando visitas');
    }
  };

  const handleDeleteCustomer = (customer) => {
    setDeleteDialog({ open: true, customer });
  };

  const confirmDeleteCustomer = async () => {
    if (!deleteDialog.customer) return;
    
    try {
      const response = await adminAPI.deleteCustomer(deleteDialog.customer.id);
      if (response.data.success) {
        toast.success(response.data.odoo_archived 
          ? 'Cliente eliminado y archivado en Odoo' 
          : 'Cliente eliminado exitosamente');
        setDeleteDialog({ open: false, customer: null });
        loadCustomers();
        // Refresh archived list if it was loaded
        if (archivedCustomers.length > 0) {
          loadArchivedCustomers();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error eliminando cliente');
    }
  };

  const handleRestoreCustomer = (customer) => {
    setRestoreFormData({ stickers: 0, gold_coins: 0 });
    setRestoreDialog({ open: true, customer });
  };

  const confirmRestoreCustomer = async () => {
    if (!restoreDialog.customer) return;
    
    try {
      const response = await adminAPI.restoreCustomer(restoreDialog.customer.id, {
        password: '1234',
        stickers: restoreFormData.stickers,
        gold_coins: restoreFormData.gold_coins
      });
      if (response.data.success) {
        const msg = restoreFormData.stickers > 0 || restoreFormData.gold_coins > 0
          ? `Cliente restaurado con ${restoreFormData.stickers} stickers y ${restoreFormData.gold_coins} monedas`
          : 'Cliente restaurado exitosamente';
        toast.success(msg);
        setRestoreDialog({ open: false, customer: null });
        loadCustomers();
        loadArchivedCustomers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error restaurando cliente');
    }
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      {/* Search Bar - Full Width */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-400" />
          <Input
            placeholder="Buscar por nombre, teléfono o email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-14 py-6 text-lg rounded-xl border-2 border-rose-soft focus:border-rose-pop w-full"
            data-testid="customer-search-input"
          />
          {search && (
            <button 
              onClick={() => setSearch('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>
      </div>
      
      {/* Header with Buttons */}
      <div className="flex justify-end items-center mb-4">
        <div className="flex gap-2">
          <Button 
            variant={showArchived ? "default" : "outline"}
            onClick={() => setShowArchived(!showArchived)}
            className={showArchived 
              ? "bg-amber-500 hover:bg-amber-600 text-white rounded-xl" 
              : "border-amber-300 text-amber-600 hover:bg-amber-50 rounded-xl"}
            data-testid="toggle-archived-btn"
          >
            <Archive className="w-4 h-4 mr-2" />
            {showArchived ? 'Ver Activos' : 'Ver Archivados'}
          </Button>
          <Button 
            onClick={openCreateDialog}
            className="bg-rose-pop hover:bg-rose-dark text-white rounded-xl"
            data-testid="create-customer-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Cliente
          </Button>
        </div>
      </div>

      {/* Archived Customers Section */}
      {showArchived && (
        <Card className="border-2 border-amber-200 rounded-2xl overflow-hidden bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="font-nunito text-lg flex items-center gap-2">
              <Archive className="w-5 h-5 text-amber-600" />
              Clientes Archivados en Odoo
            </CardTitle>
            <CardDescription>
              Estos clientes fueron eliminados de la app pero siguen archivados en Odoo
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingArchived ? (
              <div className="flex justify-center py-8">
                <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : archivedCustomers.length === 0 ? (
              <p className="text-center py-8 text-gray-500">No hay clientes archivados</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-amber-100">
                    <TableHead className="font-nunito font-bold">Nombre</TableHead>
                    <TableHead className="font-nunito font-bold">Email</TableHead>
                    <TableHead className="font-nunito font-bold">Teléfono</TableHead>
                    <TableHead className="font-nunito font-bold text-center">ID Odoo</TableHead>
                    <TableHead className="font-nunito font-bold text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {archivedCustomers.map((customer) => (
                    <TableRow key={customer.id} className="hover:bg-amber-100/50 bg-white">
                      <TableCell className="font-quicksand font-medium">{customer.name}</TableCell>
                      <TableCell className="text-gray-600">{customer.email || '-'}</TableCell>
                      <TableCell className="text-gray-600">{customer.phone || '-'}</TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-amber-200 text-amber-800">ID: {customer.id}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRestoreCustomer(customer)}
                          className="text-green-600 hover:bg-green-50"
                          data-testid={`restore-customer-${customer.id}`}
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Restaurar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <div className="flex justify-end mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={loadArchivedCustomers}
                disabled={loadingArchived}
                className="border-amber-300 text-amber-600 hover:bg-amber-100"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingArchived ? 'animate-spin' : ''}`} />
                Actualizar lista
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Customers Table */}
      <Card className="border-2 border-rose-soft rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-rose-faint">
              <TableHead className="font-nunito font-bold">Nombre</TableHead>
              <TableHead className="font-nunito font-bold">Contacto</TableHead>
              <TableHead className="font-nunito font-bold text-center">Stickers</TableHead>
              <TableHead className="font-nunito font-bold text-center">🪙 Monedas</TableHead>
              <TableHead className="font-nunito font-bold text-center">Odoo</TableHead>
              <TableHead className="font-nunito font-bold text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  <div className="w-6 h-6 border-2 border-rose-pop border-t-transparent rounded-full animate-spin mx-auto" />
                </TableCell>
              </TableRow>
            ) : customers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                  No se encontraron clientes
                </TableCell>
              </TableRow>
            ) : (
              customers.map((customer) => (
                <TableRow key={customer.id} className="hover:bg-rose-faint/50">
                  <TableCell className="font-quicksand font-medium">
                    {customer.name}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <p className="text-gray-700">{customer.phone || '-'}</p>
                      <p className="text-gray-500">{customer.email || '-'}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="secondary" className="bg-rose-soft text-rose-dark font-bold">
                      {customer.stickers || 0}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge className="bg-amber-100 text-amber-700 font-bold">
                      🪙 {customer.gold_coins || 0}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    {customer.odoo_partner_id ? (
                      <Badge className="bg-mint text-gray-900">
                        ID: {customer.odoo_partner_id}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="border-rose-soft text-gray-500">
                        Sin sync
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(customer)}
                      className="hover:bg-rose-faint"
                      data-testid={`edit-stickers-${customer.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSyncToOdoo(customer.id)}
                      className="hover:bg-rose-faint"
                      data-testid={`sync-odoo-${customer.id}`}
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteCustomer(customer)}
                      className="text-red-500 hover:bg-red-50"
                      data-testid={`delete-customer-${customer.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Load More Button */}
      {!search.trim() && hasMore && (
        <div className="flex justify-center mt-4">
          <Button
            onClick={loadMoreCustomers}
            disabled={loading}
            variant="outline"
            className="rounded-xl border-rose-soft hover:bg-rose-faint"
          >
            {loading ? 'Cargando...' : `Cargar más (${allCustomers.length} de ${totalCustomers})`}
          </Button>
        </div>
      )}

      {/* Customer count */}
      <div className="text-center text-sm text-gray-500 mt-2 mb-4">
        {searchLoading ? (
          <span>Buscando en todos los clientes...</span>
        ) : (
          <span>Mostrando {customers.length} {search.trim() ? 'resultados' : `de ${totalCustomers} clientes`}</span>
        )}
      </div>

      {/* Create Customer Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="bg-white rounded-2xl border-2 border-rose-soft">
          <DialogHeader>
            <DialogTitle className="font-nunito text-xl">Crear Nuevo Cliente</DialogTitle>
            <DialogDescription>Ingresa los datos del nuevo cliente</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nombre *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="rounded-xl border-rose-soft"
                placeholder="Nombre completo"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="rounded-xl border-rose-soft"
                placeholder="correo@ejemplo.com"
              />
            </div>
            <div>
              <Label>Teléfono</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="rounded-xl border-rose-soft"
                placeholder="Opcional"
              />
            </div>
            <div>
              <Label>Contraseña inicial</Label>
              <Input
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="rounded-xl border-rose-soft"
                placeholder="1234"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Stickers iniciales</Label>
                <Input
                  type="number"
                  min="0"
                  max="50"
                  value={formData.stickers}
                  onChange={(e) => setFormData({...formData, stickers: parseInt(e.target.value) || 0})}
                  className="rounded-xl border-rose-soft"
                />
              </div>
              <div>
                <Label>🪙 Monedas</Label>
                <Input
                  type="number"
                  min="0"
                  max="10"
                  value={formData.gold_coins}
                  onChange={(e) => setFormData({...formData, gold_coins: parseInt(e.target.value) || 0})}
                  className="rounded-xl border-rose-soft"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateCustomer} className="bg-rose-pop hover:bg-rose-dark text-white">
              Crear Cliente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Visits Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="bg-white rounded-2xl border-2 border-rose-soft">
          <DialogHeader>
            <DialogTitle className="font-nunito text-xl">Editar Stickers</DialogTitle>
            <DialogDescription>
              Actualizar visitas y monedas de {selectedCustomer?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Stickers (0-10)</Label>
              <Input
                type="number"
                min="0"
                max="10"
                value={formData.stickers}
                onChange={(e) => setFormData({...formData, stickers: parseInt(e.target.value) || 0})}
                className="rounded-xl border-rose-soft"
              />
            </div>
            <div>
              <Label>🪙 Monedas de Oro (0-5)</Label>
              <Input
                type="number"
                min="0"
                max="10"
                value={formData.gold_coins}
                onChange={(e) => setFormData({...formData, gold_coins: parseInt(e.target.value) || 0})}
                className="rounded-xl border-rose-soft"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>Cancelar</Button>
            <Button onClick={handleUpdateVisits} className="bg-rose-pop hover:bg-rose-dark text-white">
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Customer Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, customer: null })}>
        <DialogContent className="bg-white rounded-2xl border-2 border-red-200 max-w-sm" aria-describedby="delete-customer-description">
          <DialogHeader>
            <DialogTitle className="font-nunito text-xl text-red-600">Eliminar Cliente</DialogTitle>
            <DialogDescription id="delete-customer-description">
              ¿Estás seguro de eliminar a <strong>{deleteDialog.customer?.name}</strong>?
              {deleteDialog.customer?.odoo_partner_id && (
                <span className="block mt-2 text-sm">
                  También se archivará en Odoo (ID: {deleteDialog.customer.odoo_partner_id})
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialog({ open: false, customer: null })}
              data-testid="cancel-delete-customer-btn"
            >
              Cancelar
            </Button>
            <Button 
              onClick={confirmDeleteCustomer} 
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-customer-btn"
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Customer Confirmation Dialog */}
      <Dialog open={restoreDialog.open} onOpenChange={(open) => !open && setRestoreDialog({ open: false, customer: null })}>
        <DialogContent className="bg-white rounded-2xl border-2 border-green-200 max-w-md" aria-describedby="restore-customer-description">
          <DialogHeader>
            <DialogTitle className="font-nunito text-xl text-green-600">Restaurar Cliente</DialogTitle>
            <DialogDescription id="restore-customer-description">
              Restaurar a <strong>{restoreDialog.customer?.name}</strong> de Odoo
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-600">
              Contraseña por defecto: <strong>1234</strong>
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-quicksand">Stickers iniciales (0-10)</Label>
                <Input
                  type="number"
                  min="0"
                  max="10"
                  value={restoreFormData.stickers}
                  onChange={(e) => setRestoreFormData({
                    ...restoreFormData, 
                    stickers: Math.min(10, Math.max(0, parseInt(e.target.value) || 0))
                  })}
                  className="mt-1 rounded-xl border-green-200 focus:border-green-400"
                  data-testid="restore-stickers-input"
                />
              </div>
              <div>
                <Label className="text-sm font-quicksand">🪙 Monedas (0-5)</Label>
                <Input
                  type="number"
                  min="0"
                  max="10"
                  value={restoreFormData.gold_coins}
                  onChange={(e) => setRestoreFormData({
                    ...restoreFormData, 
                    gold_coins: Math.min(10, Math.max(0, parseInt(e.target.value) || 0))
                  })}
                  className="mt-1 rounded-xl border-green-200 focus:border-green-400"
                  data-testid="restore-coins-input"
                />
              </div>
            </div>
            
            <p className="text-xs text-gray-500">
              Opcional: Asigna visitas y monedas para reconocer el historial anterior del cliente.
            </p>
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setRestoreDialog({ open: false, customer: null })}
              data-testid="cancel-restore-customer-btn"
            >
              Cancelar
            </Button>
            <Button 
              onClick={confirmRestoreCustomer} 
              className="bg-green-500 hover:bg-green-600 text-white"
              data-testid="confirm-restore-customer-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Restaurar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Promotions Page
const PromotionsPage = () => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    active: true
  });

  const loadPromotions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await promotionAPI.list(false);
      setPromotions(response.data);
    } catch (error) {
      toast.error('Error cargando promociones');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPromotions();
  }, [loadPromotions]);

  const openCreateDialog = () => {
    setSelectedPromo(null);
    setImageFile(null);
    setImagePreview(null);
    setFormData({ title: '', description: '', image_url: '', active: true });
    setEditDialog(true);
  };

  const openEditDialog = (promo) => {
    setSelectedPromo(promo);
    setImageFile(null);
    setImagePreview(promo.image_url || null);
    setFormData({
      title: promo.title,
      description: promo.description || '',
      image_url: promo.image_url,
      active: promo.active
    });
    setEditDialog(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!formData.title || (!formData.image_url && !imageFile)) {
      toast.error('Título e imagen son requeridos');
      return;
    }

    try {
      setUploading(true);
      let finalImageUrl = formData.image_url;

      if (imageFile) {
        const uploadResponse = await promotionAPI.uploadImage(imageFile);
        finalImageUrl = uploadResponse.data.url;
      }

      const promoData = { ...formData, image_url: finalImageUrl };

      if (selectedPromo) {
        await promotionAPI.update(selectedPromo.id, promoData);
        toast.success('Promoción actualizada');
      } else {
        await promotionAPI.create(promoData);
        toast.success('Promoción creada');
      }
      setEditDialog(false);
      setImageFile(null);
      setImagePreview(null);
      loadPromotions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error guardando promoción');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta promoción?')) return;
    
    try {
      await promotionAPI.delete(id);
      toast.success('Promoción eliminada');
      loadPromotions();
    } catch (error) {
      toast.error('Error eliminando promoción');
    }
  };

  return (
    <div className="space-y-6" data-testid="promotions-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <p className="text-gray-500 font-quicksand">
          {promotions.length} promociones
        </p>
        <Button
          onClick={openCreateDialog}
          className="bg-rose-pop hover:bg-rose-dark text-white rounded-xl click-squish"
          data-testid="create-promo-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nueva Promoción
        </Button>
      </div>

      {/* Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {loading ? (
          <div className="col-span-full flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-rose-pop border-t-transparent rounded-full animate-spin" />
          </div>
        ) : promotions.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500">
            No hay promociones. Crea la primera!
          </div>
        ) : (
          promotions.map((promo) => (
            <Card key={promo.id} className="border-2 border-rose-soft rounded-2xl overflow-hidden">
              <div className="h-32 overflow-hidden">
                <img
                  src={promo.image_url}
                  alt={promo.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1497034825429-c343d7c6a68f?w=400';
                  }}
                />
              </div>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-nunito font-bold text-gray-900">{promo.title}</h3>
                  <Badge className={promo.active ? 'bg-mint' : 'bg-gray-300'}>
                    {promo.active ? 'Activa' : 'Inactiva'}
                  </Badge>
                </div>
                <p className="text-sm text-gray-500 mb-4 line-clamp-2">{promo.description}</p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(promo)}
                    className="flex-1 border-rose-soft hover:bg-rose-faint rounded-lg"
                    data-testid={`edit-promo-${promo.id}`}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(promo.id)}
                    className="text-red-500 hover:bg-red-50 rounded-lg"
                    data-testid={`delete-promo-${promo.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="bg-white rounded-2xl max-w-md" aria-describedby="promo-dialog-description">
          <DialogHeader>
            <DialogTitle className="font-nunito">
              {selectedPromo ? 'Editar Promoción' : 'Nueva Promoción'}
            </DialogTitle>
            <DialogDescription id="promo-dialog-description" className="text-gray-500 font-quicksand">
              {selectedPromo ? 'Modifica los datos de la promoción' : 'Crea una nueva promoción para mostrar a tus clientes'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="font-quicksand">Título</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Ej: 2x1 en Conos"
                className="mt-1 rounded-xl border-rose-soft"
                data-testid="promo-title-input"
              />
            </div>
            <div>
              <Label className="font-quicksand">Descripción</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descripción de la promoción..."
                className="mt-1 rounded-xl border-rose-soft"
                data-testid="promo-description-input"
              />
            </div>
            
            {/* Image Upload */}
            <div>
              <Label className="font-quicksand">Imagen de la Promoción</Label>
              <div className="mt-2 space-y-3">
                {imagePreview && (
                  <div className="relative w-full h-32 rounded-xl overflow-hidden bg-gray-100">
                    <img 
                      src={imagePreview.startsWith('data:') || imagePreview.startsWith('http') 
                        ? imagePreview 
                        : `${process.env.REACT_APP_BACKEND_URL}${imagePreview}`
                      } 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setImageFile(null);
                        setImagePreview(null);
                        setFormData(prev => ({ ...prev, image_url: '' }));
                      }}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <Input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleImageChange}
                  className="rounded-xl border-rose-soft file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-rose-50 file:text-rose-700 hover:file:bg-rose-100"
                  data-testid="promo-image-input"
                />
                <p className="text-xs text-gray-500">
                  Formatos permitidos: JPG, PNG, WebP, GIF
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label className="font-quicksand">Activa</Label>
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, active: checked }))}
                data-testid="promo-active-switch"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditDialog(false)}
              className="rounded-xl border-rose-soft"
              disabled={uploading}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              className="bg-rose-pop hover:bg-rose-dark text-white rounded-xl"
              data-testid="save-promo-btn"
              disabled={uploading}
            >
              {uploading ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Rewards Page
const RewardsPage = () => {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedReward, setSelectedReward] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    reward_type: 'product',
    value: '',
    currency_type: 'stickers',
    cost: 5,
    active: true,
    imageUrl: ''
  });

  const loadRewards = useCallback(async () => {
    setLoading(true);
    try {
      const response = await rewardAPI.list(false);
      setRewards(response.data);
    } catch (error) {
      toast.error('Error cargando recompensas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRewards();
  }, [loadRewards]);

  const openCreateDialog = () => {
    setSelectedReward(null);
    setImageFile(null);
    setImagePreview(null);
    setFormData({
      name: '',
      description: '',
      reward_type: 'product',
      value: '',
      currency_type: 'stickers',
      cost: 5,
      active: true,
      imageUrl: ''
    });
    setEditDialog(true);
  };

  const openEditDialog = (reward) => {
    setSelectedReward(reward);
    setImageFile(null);
    setImagePreview(reward.imageUrl || null);
    setFormData({
      name: reward.name,
      description: reward.description || '',
      reward_type: reward.reward_type,
      value: reward.value,
      currency_type: reward.currency_type || 'coins',
      cost: reward.cost || 1,
      active: reward.active,
      imageUrl: reward.imageUrl || ''
    });
    setEditDialog(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.value) {
      toast.error('Nombre y valor son requeridos');
      return;
    }

    try {
      setUploading(true);
      let finalImageUrl = formData.imageUrl;

      // Upload image if a new file was selected
      if (imageFile) {
        const uploadResponse = await rewardAPI.uploadImage(imageFile);
        finalImageUrl = uploadResponse.data.url;
      }

      const rewardData = { ...formData, imageUrl: finalImageUrl };

      if (selectedReward) {
        await rewardAPI.update(selectedReward.id, rewardData);
        toast.success('Recompensa actualizada');
      } else {
        await rewardAPI.create(rewardData);
        toast.success('Recompensa creada');
      }
      setEditDialog(false);
      setImageFile(null);
      setImagePreview(null);
      loadRewards();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error guardando recompensa');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta recompensa?')) return;
    
    try {
      await rewardAPI.delete(id);
      toast.success('Recompensa eliminada');
      loadRewards();
    } catch (error) {
      toast.error('Error eliminando recompensa');
    }
  };

  return (
    <div className="space-y-6" data-testid="rewards-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <p className="text-gray-500 font-quicksand">
          {rewards.length} recompensas configuradas
        </p>
        <Button
          onClick={openCreateDialog}
          className="bg-rose-pop hover:bg-rose-dark text-white rounded-xl click-squish"
          data-testid="create-reward-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nueva Recompensa
        </Button>
      </div>

      {/* List */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-rose-pop border-t-transparent rounded-full animate-spin" />
          </div>
        ) : rewards.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No hay recompensas configuradas
          </div>
        ) : (
          rewards.map((reward) => (
            <Card key={reward.id} className="border-2 border-rose-soft rounded-2xl">
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  {/* Show reward image if available, otherwise show currency icon */}
                  {reward.imageUrl ? (
                    <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0">
                      <img 
                        src={reward.imageUrl.startsWith('http') 
                          ? reward.imageUrl 
                          : `${process.env.REACT_APP_BACKEND_URL}${reward.imageUrl}`
                        }
                        alt={reward.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      reward.currency_type === 'stickers' ? 'bg-rose-100' : 'bg-amber-100'
                    }`}>
                      {reward.currency_type === 'stickers' ? (
                        <span className="text-2xl">🎟️</span>
                      ) : (
                        <span className="text-2xl"><img src="https://hosting.photobucket.com/d970c25b-dfba-4164-a867-030817bbaa89/005fa523-d211-4211-ba7f-a45ade3246f1.png" alt="moneda" className="w-8 h-8 inline" /></span>
                      )}
                    </div>
                  )}
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-nunito font-bold text-gray-900">{reward.name}</h3>
                      <Badge className={reward.active ? 'bg-mint' : 'bg-gray-300'}>
                        {reward.active ? 'Activa' : 'Inactiva'}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500">{reward.description}</p>
                    <div className="flex items-center gap-4 mt-2">
                      <Badge variant="outline" className="border-rose-soft">
                        {reward.reward_type === 'product' ? '🍦 Producto' : '💰 Descuento'}
                      </Badge>
                      <span className="text-sm font-bold text-rose-pop">
                        {reward.reward_type === 'product' ? reward.value : `${reward.value}%`}
                      </span>
                      <span className={`text-sm font-bold ${reward.currency_type === 'stickers' ? 'text-rose-500' : 'text-amber-600'}`}>
                        {reward.currency_type === 'stickers' ? `${reward.cost} 🎟️ stickers` : <span className="inline-flex items-center gap-1">{reward.cost} <img src="https://hosting.photobucket.com/d970c25b-dfba-4164-a867-030817bbaa89/005fa523-d211-4211-ba7f-a45ade3246f1.png" alt="moneda" className="w-4 h-4 inline" /> monedas</span>}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditDialog(reward)}
                      className="border-rose-soft hover:bg-rose-faint rounded-lg"
                      data-testid={`edit-reward-${reward.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(reward.id)}
                      className="text-red-500 hover:bg-red-50 rounded-lg"
                      data-testid={`delete-reward-${reward.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="bg-white rounded-2xl max-w-md" aria-describedby="reward-dialog-description">
          <DialogHeader>
            <DialogTitle className="font-nunito">
              {selectedReward ? 'Editar Recompensa' : 'Nueva Recompensa'}
            </DialogTitle>
            <DialogDescription id="reward-dialog-description" className="text-gray-500 font-quicksand">
              {selectedReward ? 'Modifica los datos de la recompensa' : 'Configura una nueva recompensa para tus clientes'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="font-quicksand">Nombre</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ej: Helado Gratis"
                className="mt-1 rounded-xl border-rose-soft"
                data-testid="reward-name-input"
              />
            </div>
            <div>
              <Label className="font-quicksand">Descripción</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descripción opcional..."
                className="mt-1 rounded-xl border-rose-soft"
                data-testid="reward-description-input"
              />
            </div>
            
            {/* Image Upload */}
            <div>
              <Label className="font-quicksand">Imagen del Premio</Label>
              <div className="mt-2 space-y-3">
                {imagePreview && (
                  <div className="relative w-full h-32 rounded-xl overflow-hidden bg-gray-100">
                    <img 
                      src={imagePreview.startsWith('data:') || imagePreview.startsWith('http') 
                        ? imagePreview 
                        : `${process.env.REACT_APP_BACKEND_URL}${imagePreview}`
                      } 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setImageFile(null);
                        setImagePreview(null);
                        setFormData(prev => ({ ...prev, imageUrl: '' }));
                      }}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <Input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleImageChange}
                  className="rounded-xl border-rose-soft file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-rose-50 file:text-rose-700 hover:file:bg-rose-100"
                  data-testid="reward-image-input"
                />
                <p className="text-xs text-gray-500">
                  Formatos permitidos: JPG, PNG, WebP, GIF
                </p>
              </div>
            </div>
            <div>
              <Label className="font-quicksand">Tipo de Recompensa</Label>
              <Select
                value={formData.reward_type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, reward_type: value }))}
              >
                <SelectTrigger className="mt-1 rounded-xl border-rose-soft" data-testid="reward-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="product">Producto Gratis</SelectItem>
                  <SelectItem value="discount">Descuento %</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-quicksand">
                {formData.reward_type === 'product' ? 'Producto' : 'Porcentaje de Descuento'}
              </Label>
              <Input
                value={formData.value}
                onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value }))}
                placeholder={formData.reward_type === 'product' ? 'Ej: Cono Sencillo' : 'Ej: 15'}
                className="mt-1 rounded-xl border-rose-soft"
                data-testid="reward-value-input"
              />
            </div>
            
            {/* Currency Type Selection */}
            <div>
              <Label className="font-quicksand">Tipo de Moneda</Label>
              <Select
                value={formData.currency_type}
                onValueChange={(value) => setFormData(prev => ({ 
                  ...prev, 
                  currency_type: value,
                  cost: value === 'stickers' ? Math.min(prev.cost, 10) : Math.min(prev.cost, 5)
                }))}
              >
                <SelectTrigger className="mt-1 rounded-xl border-rose-soft" data-testid="reward-currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="stickers">🎟️ Stickers (1-10)</SelectItem>
                  <SelectItem value="coins">🪙 Monedas de Oro (1-10)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Cost Input */}
            <div>
              <Label className="font-quicksand">
                Costo ({formData.currency_type === 'stickers' ? '1-10 stickers' : '1-10 monedas'})
              </Label>
              <Input
                type="number"
                min="1"
                max={formData.currency_type === 'stickers' ? 10 : 10}
                value={formData.cost}
                onChange={(e) => {
                  const val = e.target.value;
                  const max = formData.currency_type === 'stickers' ? 10 : 10;
                  if (val === '') {
                    setFormData(prev => ({ ...prev, cost: '' }));
                  } else {
                    const num = parseInt(val);
                    if (!isNaN(num)) {
                      setFormData(prev => ({ ...prev, cost: Math.min(Math.max(1, num), max) }));
                    }
                  }
                }}
                onBlur={(e) => {
                  if (e.target.value === '' || parseInt(e.target.value) < 1) {
                    setFormData(prev => ({ ...prev, cost: 1 }));
                  }
                }}
                className="mt-1 rounded-xl border-rose-soft"
                data-testid="reward-cost-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.currency_type === 'stickers' 
                  ? 'Los clientes usarán stickers de su progreso actual'
                  : 'Los clientes usarán monedas de oro acumuladas'}
              </p>
            </div>
            
            <div className="flex items-center justify-between">
              <Label className="font-quicksand">Activa</Label>
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, active: checked }))}
                data-testid="reward-active-switch"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditDialog(false)}
              className="rounded-xl border-rose-soft"
              disabled={uploading}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              className="bg-rose-pop hover:bg-rose-dark text-white rounded-xl"
              data-testid="save-reward-btn"
              disabled={uploading}
            >
              {uploading ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Admins Page
const AdminsPage = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, admin: null });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'manager'
  });
  
  // Get current admin from localStorage
  const currentAdmin = JSON.parse(localStorage.getItem('darkar_admin') || '{}');

  const loadAdmins = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminAPI.listAdmins(currentAdmin.id);
      setAdmins(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error cargando usuarios');
    } finally {
      setLoading(false);
    }
  }, [currentAdmin.id]);

  useEffect(() => {
    loadAdmins();
  }, [loadAdmins]);

  const openCreateDialog = () => {
    setFormData({ name: '', email: '', password: '', role: 'manager' });
    setCreateDialog(true);
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Todos los campos son requeridos');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      await adminAPI.createAdmin(formData, currentAdmin.id);
      toast.success(formData.role === 'admin' ? 'Administrador creado' : 'Gerente creado');
      setCreateDialog(false);
      loadAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creando usuario');
    }
  };

  const handleDelete = async (adminId, adminEmail) => {
    setDeleteDialog({ open: true, admin: { id: adminId, email: adminEmail } });
  };

  const confirmDelete = async () => {
    if (!deleteDialog.admin) return;
    
    try {
      await adminAPI.deleteAdmin(deleteDialog.admin.id, currentAdmin.id);
      toast.success('Usuario eliminado');
      setDeleteDialog({ open: false, admin: null });
      loadAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error eliminando usuario');
    }
  };

  return (
    <div className="space-y-6" data-testid="admins-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <p className="text-gray-500 font-quicksand">
          {admins.length} usuario(s) del panel
        </p>
        <Button
          onClick={openCreateDialog}
          className="bg-rose-pop hover:bg-rose-dark text-white rounded-xl click-squish"
          data-testid="create-admin-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Admin
        </Button>
      </div>

      {/* List */}
      <Card className="border-2 border-rose-soft rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-rose-faint">
              <TableHead className="font-nunito font-bold">Nombre</TableHead>
              <TableHead className="font-nunito font-bold">Email</TableHead>
              <TableHead className="font-nunito font-bold">Rol</TableHead>
              <TableHead className="font-nunito font-bold">Fecha Creación</TableHead>
              <TableHead className="font-nunito font-bold text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  <div className="w-6 h-6 border-2 border-rose-pop border-t-transparent rounded-full animate-spin mx-auto" />
                </TableCell>
              </TableRow>
            ) : admins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                  No hay usuarios del panel
                </TableCell>
              </TableRow>
            ) : (
              admins.map((a) => (
                <TableRow key={a.id} className="hover:bg-rose-faint/50">
                  <TableCell className="font-quicksand font-medium">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${a.role === 'admin' ? 'bg-rose-pop' : 'bg-amber-500'}`}>
                        <Shield className="w-5 h-5 text-white" />
                      </div>
                      {a.name}
                    </div>
                  </TableCell>
                  <TableCell className="text-gray-600">{a.email}</TableCell>
                  <TableCell>
                    <Badge className={a.role === 'admin' ? 'bg-rose-pop text-white' : 'bg-amber-500 text-white'}>
                      {a.role === 'admin' ? 'Administrador' : 'Gerente'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-gray-500 text-sm">
                    {new Date(a.created_at).toLocaleDateString('es-ES', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(a.id, a.email)}
                      className="text-red-500 hover:bg-red-50 rounded-lg"
                      data-testid={`delete-admin-${a.id}`}
                      disabled={a.id === currentAdmin.id || (a.role === 'admin' && admins.filter(x => x.role === 'admin').length <= 1)}
                      title={a.id === currentAdmin.id ? 'No puedes eliminarte' : ''}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="bg-white rounded-2xl border-2 border-rose-soft" aria-describedby="admin-create-description">
          <DialogHeader>
            <DialogTitle className="font-nunito text-xl">Nuevo Usuario del Panel</DialogTitle>
            <DialogDescription id="admin-create-description">
              Crea un administrador o gerente para acceder al panel
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nombre *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="rounded-xl border-rose-soft"
                placeholder="Nombre completo"
                data-testid="admin-name-input"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="rounded-xl border-rose-soft"
                placeholder="usuario@ejemplo.com"
                data-testid="admin-email-create-input"
              />
            </div>
            <div>
              <Label>Contraseña *</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="rounded-xl border-rose-soft"
                placeholder="Mínimo 6 caracteres"
                data-testid="admin-password-create-input"
              />
            </div>
            <div>
              <Label>Rol *</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({...formData, role: value})}
              >
                <SelectTrigger className="rounded-xl border-rose-soft" data-testid="admin-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                      Gerente - Acceso al panel (sin gestión de usuarios)
                    </div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-rose-pop"></span>
                      Administrador - Control total
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreate} className="bg-rose-pop hover:bg-rose-dark text-white">
              Crear {formData.role === 'admin' ? 'Administrador' : 'Gerente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, admin: null })}>
        <DialogContent className="bg-white rounded-2xl border-2 border-red-200 max-w-sm" aria-describedby="delete-confirm-description">
          <DialogHeader>
            <DialogTitle className="font-nunito text-xl text-red-600">Confirmar Eliminación</DialogTitle>
            <DialogDescription id="delete-confirm-description">
              ¿Estás seguro de eliminar a <strong>{deleteDialog.admin?.email}</strong>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialog({ open: false, admin: null })}
              data-testid="cancel-delete-btn"
            >
              Cancelar
            </Button>
            <Button 
              onClick={confirmDelete} 
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-btn"
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Odoo Webhook Page
const OdooWebhookPage = () => {
  const [copied, setCopied] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testPartnerId, setTestPartnerId] = useState('');
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const WEBHOOK_VISIT = `${API_BASE}/api/odoo/webhook/visit`;
  const WEBHOOK_CUSTOMER = `${API_BASE}/api/odoo/webhook/customer`;

  // Load sync status on mount and every 30 seconds
  useEffect(() => {
    const loadSyncStatus = async () => {
      try {
        const response = await adminAPI.getSyncStatus();
        setSyncStatus(response.data);
      } catch (error) {
        console.error('Error loading sync status:', error);
      }
    };
    
    loadSyncStatus();
    const interval = setInterval(loadSyncStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleManualSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    
    try {
      const response = await adminAPI.syncPOS();
      setSyncResult(response.data);
      
      if (response.data.new_visits > 0) {
        toast.success(`¡${response.data.new_visits} nuevas visitas registradas!`);
      } else {
        toast.info('No hay nuevas visitas para registrar');
      }
      
      // Refresh sync status
      const statusResponse = await adminAPI.getSyncStatus();
      setSyncStatus(statusResponse.data);
    } catch (error) {
      toast.error('Error al sincronizar');
      setSyncResult({ success: false, error: error.message });
    } finally {
      setSyncing(false);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
    toast.success('Copiado al portapapeles');
  };

  const testWebhook = async () => {
    if (!testPartnerId) {
      toast.error('Ingresa un Odoo Partner ID');
      return;
    }
    
    setTesting(true);
    setTestResult(null);
    
    try {
      const response = await fetch(`${API_BASE}/api/odoo/webhook/visit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          odoo_partner_id: parseInt(testPartnerId), 
          order_ref: 'TEST/MANUAL/' + Date.now() 
        })
      });
      
      const data = await response.json();
      setTestResult({ success: response.ok, data });
      
      if (response.ok) {
        toast.success(`Visita registrada para ${data.customer_name}`);
      } else {
        toast.error(data.detail || 'Error en el webhook');
      }
    } catch (error) {
      setTestResult({ success: false, data: { error: error.message } });
      toast.error('Error de conexión');
    } finally {
      setTesting(false);
    }
  };

  const pythonCodeVisit = `# Código para Acción Automatizada en Odoo
# Modelo: pos.order
# Acción: Al crear (o al escribir cuando state = 'paid')

import requests
import json

WEBHOOK_URL = "${WEBHOOK_VISIT}"

for record in records:
    if record.partner_id:
        try:
            data = {
                "odoo_partner_id": record.partner_id.id,
                "order_ref": record.name
            }
            requests.post(
                WEBHOOK_URL,
                json=data,
                timeout=5
            )
        except Exception as e:
            # Log error pero no bloquear la venta
            pass`;

  const pythonCodeCustomer = `# Código para sincronizar clientes
# Modelo: res.partner
# Acción: Al crear

import requests

WEBHOOK_URL = "${WEBHOOK_CUSTOMER}"

for record in records:
    if record.customer_rank > 0:  # Solo clientes
        try:
            data = {
                "odoo_id": record.id,
                "name": record.name,
                "phone": record.phone or "",
                "email": record.email or ""
            }
            requests.post(
                WEBHOOK_URL,
                json=data,
                timeout=5
            )
        except:
            pass`;

  return (
    <div className="space-y-6" data-testid="odoo-page">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-nunito font-bold text-gray-900">Integración con Odoo</h2>
        <p className="text-gray-500 font-quicksand">Sincroniza automáticamente las ventas del POS</p>
      </div>

      {/* Sync Status Card */}
      <Card className="border-2 border-green-200 rounded-2xl bg-green-50">
        <CardHeader>
          <CardTitle className="font-nunito flex items-center gap-2">
            <RefreshCw className={`w-5 h-5 text-green-600 ${syncing ? 'animate-spin' : ''}`} />
            Sincronización Automática
          </CardTitle>
          <CardDescription>
            Las ventas del POS se sincronizan automáticamente cada minuto
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-white rounded-xl">
            <div>
              <p className="text-sm text-gray-600">Estado de Odoo</p>
              <p className={`font-medium ${syncStatus?.odoo_connected ? 'text-green-600' : 'text-red-500'}`}>
                {syncStatus?.odoo_connected ? '● Conectado' : '○ Desconectado'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Última sincronización</p>
              <p className="font-medium text-gray-900">
                {syncStatus?.last_sync 
                  ? new Date(syncStatus.last_sync).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
                  : 'Nunca'}
              </p>
            </div>
            <Button 
              onClick={handleManualSync}
              disabled={syncing || !syncStatus?.odoo_connected}
              className="bg-green-500 hover:bg-green-600 text-white rounded-xl"
              data-testid="sync-now-btn"
            >
              {syncing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sincronizando...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Sincronizar Ahora
                </>
              )}
            </Button>
          </div>
          
          {syncResult && (
            <div className={`p-4 rounded-xl ${syncResult.success ? 'bg-green-100' : 'bg-red-100'}`}>
              <p className="font-medium">
                {syncResult.success 
                  ? `✓ Órdenes revisadas: ${syncResult.orders_checked} | Nuevas visitas: ${syncResult.new_visits}`
                  : `✗ Error: ${syncResult.error}`}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* URLs de Webhook */}
      <Card className="border-2 border-rose-soft rounded-2xl">
        <CardHeader>
          <CardTitle className="font-nunito flex items-center gap-2">
            <Link2 className="w-5 h-5 text-rose-pop" />
            URLs de los Webhooks
          </CardTitle>
          <CardDescription>Usa estas URLs en tus acciones automatizadas de Odoo</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm text-gray-600">Webhook de Visitas (POS)</Label>
            <div className="flex gap-2 mt-1">
              <Input 
                value={WEBHOOK_VISIT} 
                readOnly 
                className="font-mono text-sm bg-gray-50 rounded-xl"
              />
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => copyToClipboard(WEBHOOK_VISIT, 'visit')}
                className="rounded-xl border-rose-soft"
              >
                {copied === 'visit' ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-sm text-gray-600">Webhook de Clientes</Label>
            <div className="flex gap-2 mt-1">
              <Input 
                value={WEBHOOK_CUSTOMER} 
                readOnly 
                className="font-mono text-sm bg-gray-50 rounded-xl"
              />
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => copyToClipboard(WEBHOOK_CUSTOMER, 'customer')}
                className="rounded-xl border-rose-soft"
              >
                {copied === 'customer' ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Probar Webhook */}
      <Card className="border-2 border-amber-200 rounded-2xl bg-amber-50">
        <CardHeader>
          <CardTitle className="font-nunito flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-amber-600" />
            Probar Webhook
          </CardTitle>
          <CardDescription>Simula una visita para verificar que funciona</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input 
              placeholder="Odoo Partner ID (ej: 25)"
              value={testPartnerId}
              onChange={(e) => setTestPartnerId(e.target.value)}
              className="rounded-xl"
              type="number"
            />
            <Button 
              onClick={testWebhook}
              disabled={testing}
              className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl"
            >
              {testing ? 'Probando...' : 'Probar Visita'}
            </Button>
          </div>
          
          {testResult && (
            <div className={`p-4 rounded-xl ${testResult.success ? 'bg-green-100' : 'bg-red-100'}`}>
              <pre className="text-sm overflow-auto">
                {JSON.stringify(testResult.data, null, 2)}
              </pre>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Instrucciones */}
      <Card className="border-2 border-rose-soft rounded-2xl">
        <CardHeader>
          <CardTitle className="font-nunito">Pasos para Configurar en Odoo</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-rose-pop text-white flex items-center justify-center font-bold shrink-0">1</div>
              <div>
                <p className="font-medium">Activar Modo Desarrollador</p>
                <p className="text-sm text-gray-500">Ajustes → Herramientas de desarrollador → Activar modo desarrollador</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-rose-pop text-white flex items-center justify-center font-bold shrink-0">2</div>
              <div>
                <p className="font-medium">Crear Acción Automatizada</p>
                <p className="text-sm text-gray-500">Menú 🐞 (bug) → Acciones automatizadas → Crear</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-rose-pop text-white flex items-center justify-center font-bold shrink-0">3</div>
              <div>
                <p className="font-medium">Configurar la Acción</p>
                <p className="text-sm text-gray-500">Nombre: "Registrar Visita Lealtad" | Modelo: pos.order | Disparador: Al crear</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-rose-pop text-white flex items-center justify-center font-bold shrink-0">4</div>
              <div>
                <p className="font-medium">Agregar Código Python</p>
                <p className="text-sm text-gray-500">Acción: Ejecutar código Python → Pegar el código de abajo</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Código Python */}
      <Card className="border-2 border-rose-soft rounded-2xl">
        <CardHeader>
          <CardTitle className="font-nunito flex items-center justify-between">
            <span>Código Python para Odoo</span>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => copyToClipboard(pythonCodeVisit, 'code')}
              className="rounded-xl border-rose-soft"
            >
              {copied === 'code' ? <CheckCircle2 className="w-4 h-4 text-green-500 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
              Copiar
            </Button>
          </CardTitle>
          <CardDescription>Pega este código en la acción automatizada de Odoo para el POS</CardDescription>
        </CardHeader>
        <CardContent>
          <pre className="bg-gray-900 text-green-400 p-4 rounded-xl text-sm overflow-auto max-h-80">
            {pythonCodeVisit}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPanel;
